import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLogin, usePostHog } from "@finitless/shared/hooks";
import { useApiStore } from "@finitless/shared/stores";
import { useToastContext } from "@finitless/shared/contexts";
import { useTranslation } from "react-i18next";
import { useUiLocale } from "../../hooks/useUiLocale";
import { getLoginPrefillFromEnv } from "../../lib/loginPrefill";
import { Button, Icon, IconButton, Input, LocaleSelector } from "../ui";

const ORDERING_DARK_LOCKUP =
  "/brand/ordering/1.2.1/logos/ordering-lockup-color-reversed.svg";

const LoginPage = ({ onLoginSuccess, logo = ORDERING_DARK_LOCKUP }) => {
  const { t } = useTranslation(["auth", "app", "toasts"]);
  const { locale, setLocale, supportedLocales } = useUiLocale();
  const defaults = getLoginPrefillFromEnv();
  const [email, setEmail] = useState(defaults.email);
  const [password, setPassword] = useState(defaults.password);
  const [project, setProject] = useState(defaults.project);
  const [showPassword, setShowPassword] = useState(false);
  const loginMutation = useLogin();
  const setProjectConfig = useApiStore((state) => state.setProject);
  const toast = useToastContext();
  const navigate = useNavigate();
  const { trackEngagement } = usePostHog();

  const handleLocaleChange = async (event) => {
    await setLocale(event.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password || !project) {
      toast.error(t("validation.fillAllFields", { ns: "auth" }));
      return;
    }

    try {
      // Set the project configuration before login
      setProjectConfig(project);

      const result = await loginMutation.mutateAsync({ email, password });

      // Track login event
      trackEngagement.login({
        user_id: result?.id,
        email,
        project,
      });

      toast.success(t("auth.loginSuccess", { ns: "toasts" }));
      if (onLoginSuccess) {
        onLoginSuccess(result);
      }
    } catch (error) {
      toast.error(error.message || t("auth.loginFailed", { ns: "toasts" }));
    }
  };

  return (
    <main className="login-page flex min-h-dvh min-h-screen items-center justify-center bg-[var(--od-ink-950)] px-4 py-5 text-[var(--od-white)] sm:px-6 sm:py-8 lg:px-8">
      <div className="w-full max-w-md space-y-3 sm:space-y-4">
        <LocaleSelector
          id="auth-locale-selector"
          label={t("language.label", { ns: "app" })}
          showLabel={false}
          showLeadingIcon={false}
          value={locale}
          onChange={handleLocaleChange}
          options={supportedLocales}
          containerClassName="w-full justify-end"
          selectClassName="h-11 min-w-[124px] rounded-[var(--od-radius-control)] border-[var(--od-ink-600)] bg-[var(--od-ink-900)] text-[13px] text-[var(--od-white)] shadow-none backdrop-blur-none focus:border-[var(--od-blue-400)] focus:ring-[var(--od-blue-600)]/30"
        />

        <section className="rounded-[var(--od-radius-panel)] border border-[var(--od-ink-600)] bg-[var(--od-ink-900)] p-5 shadow-[var(--od-shadow-1)] sm:p-7">
          {/* Logo and Header */}
          <div className="text-center">
            <div className="mx-auto mb-5 flex min-h-8 items-center justify-center">
              <img
                className="h-7 w-auto max-w-full min-w-[120px] sm:h-8"
                src={logo}
                alt="Ordering.co"
              />
            </div>
            <h1 className="text-2xl font-semibold tracking-[-0.025em] text-[var(--od-white)] sm:text-[1.75rem]">
              {t("welcomeTitle", { ns: "auth" })}
            </h1>
            <p className="mt-2 text-sm leading-6 text-[var(--od-ink-300)]">
              {t("welcomeSubtitle", { ns: "auth" })}
            </p>
          </div>

          {/* Login Form */}
          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-3">
              {/* Project Field */}
              <Input
                id="project"
                name="project"
                type="text"
                autoComplete="off"
                required
                value={project}
                onChange={(e) => setProject(e.target.value)}
                placeholder={t("placeholders.project", { ns: "auth" })}
                disabled={loginMutation.isPending}
                label={t("fields.projectName", { ns: "auth" })}
                variant="dark"
                size="lg"
                labelClassName="text-[var(--od-ink-300)]"
                className="rounded-[var(--od-radius-control)] border-[var(--od-ink-600)] bg-[var(--od-ink-800)] text-[var(--od-white)] placeholder:text-[var(--od-ink-400)] focus:border-transparent focus:ring-[var(--od-blue-600)]"
              />

              {/* Email Field */}
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("placeholders.email", { ns: "auth" })}
                disabled={loginMutation.isPending}
                label={t("fields.emailAddress", { ns: "auth" })}
                variant="dark"
                size="lg"
                labelClassName="text-[var(--od-ink-300)]"
                className="rounded-[var(--od-radius-control)] border-[var(--od-ink-600)] bg-[var(--od-ink-800)] text-[var(--od-white)] placeholder:text-[var(--od-ink-400)] focus:border-transparent focus:ring-[var(--od-blue-600)]"
              />

              {/* Password Field */}
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("placeholders.password", { ns: "auth" })}
                disabled={loginMutation.isPending}
                label={t("fields.password", { ns: "auth" })}
                variant="dark"
                size="lg"
                labelClassName="text-[var(--od-ink-300)]"
                className="rounded-[var(--od-radius-control)] border-[var(--od-ink-600)] bg-[var(--od-ink-800)] pr-14 text-[var(--od-white)] placeholder:text-[var(--od-ink-400)] focus:border-transparent focus:ring-[var(--od-blue-600)]"
                rightIcon={
                  <IconButton
                    type="button"
                    variant="ghost"
                    size="iconTouch"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loginMutation.isPending}
                    aria-pressed={showPassword}
                    aria-label={
                      showPassword
                        ? t("accessibility.hidePassword", { ns: "auth" })
                        : t("accessibility.showPassword", { ns: "auth" })
                    }
                    className="rounded-[var(--od-radius-control)] text-[var(--od-ink-300)] hover:bg-[var(--od-ink-700)] hover:text-[var(--od-white)] focus-visible:ring-[var(--od-blue-600)] motion-reduce:transition-none"
                  >
                    <Icon name={showPassword ? "EyeOff" : "Eye"} />
                  </IconButton>
                }
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              size="touch"
              disabled={loginMutation.isPending}
              aria-busy={loginMutation.isPending || undefined}
              className="w-full rounded-[var(--od-radius-control)] bg-[var(--od-blue-600)] text-[var(--od-white)] shadow-[var(--od-shadow-blue)] hover:bg-[var(--od-blue-700)] focus-visible:ring-[var(--od-blue-600)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--od-ink-900)] motion-reduce:transition-none"
            >
              {loginMutation.isPending ? (
                <span className="flex items-center">
                  <Icon
                    name="Loader2"
                    className="mr-3 -ml-1 animate-spin motion-reduce:animate-none"
                  />
                  {t("loggingIn", { ns: "auth" })}
                </span>
              ) : (
                t("loginButton", { ns: "auth" })
              )}
            </Button>

            {/* Forgot Password Link */}
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                size="touch"
                onClick={() => navigate("/password/forgot")}
                disabled={loginMutation.isPending}
                className="h-auto min-h-11 px-2 text-[var(--od-blue-300)] hover:text-[var(--od-blue-200)] focus-visible:ring-[var(--od-blue-600)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--od-ink-900)] motion-reduce:transition-none"
              >
                {t("forgotPasswordLink", { ns: "auth" })}
              </Button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
};

export default LoginPage;
